/** User guide: top 3 in L2/L3 promote; bottom 3 in L1/L2 go down. */
import moment from 'moment';
import { formatMatchDay, localToday } from './localTime';
import { playerRecordId, simplifiedPosition } from './data';

const DAY_FORMAT = 'ddd, DD MMM YYYY';

const ZONE_SIZE = 3;

export const leagueDivisionFromName = (name) => {
  const raw = String(name || '');
  const label = raw.toLowerCase().replace(/[_-]/g, ' ');
  if (/league\s*3|\bl3\b/.test(label) || /🥉|3️⃣|3\uFE0F?\u20E3/.test(raw))
    return 3;
  if (/league\s*2|\bl2\b/.test(label) || /🥈|2️⃣|2\uFE0F?\u20E3/.test(raw))
    return 2;
  if (/league\s*1|\bl1\b/.test(label) || /🥇|1️⃣|1\uFE0F?\u20E3/.test(raw))
    return 1;
  return null;
};

export const stripLeagueDecor = (name) =>
  String(name || '')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/\d\uFE0F?\u20E3/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** Daily salary cap by division (app/league rule, not an API field). */
export const RECOMMENDED_DAILY_SALARY = {
  1: 1000000,
  2: 800000,
  3: 500000,
};

export const leagueNameFromUniverses = (code, universes = []) => {
  if (!code) return null;
  const list = universes || [];
  const needle = String(code);
  for (let i = 0; i < list.length; i += 1) {
    const leagues = list[i].leagues || [];
    const match = leagues.find(
      (league) =>
        league.code === needle ||
        String(league._id) === needle
    );
    if (match?.name) return match.name;
  }
  return null;
};

export const recommendedDailySalaryForLeague = (...labels) => {
  for (let i = 0; i < labels.length; i += 1) {
    const division = leagueDivisionFromName(labels[i]);
    if (division) return RECOMMENDED_DAILY_SALARY[division];
  }
  return null;
};

const teamId = (team) => {
  if (team == null) return '';
  if (typeof team === 'string' || typeof team === 'number') return String(team);
  if (team._id != null) return String(team._id);
  if (team.id != null) return String(team.id);
  return '';
};

const finiteGoals = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Prefer API fields; otherwise sum finished fixture scores. */
export const goalsForTeam = (team, fixtureDays) => {
  const fromApiGf = finiteGoals(
    team?.gf ?? team?.GF ?? team?.goals_for ?? team?.goalsFor
  );
  const fromApiGa = finiteGoals(
    team?.ga ?? team?.GA ?? team?.goals_against ?? team?.goalsAgainst
  );
  if (fromApiGf != null && fromApiGa != null) {
    return { gf: fromApiGf, ga: fromApiGa };
  }
  const id = teamId(team);
  let gf = 0;
  let ga = 0;
  if (!id || !Array.isArray(fixtureDays)) return { gf: 0, ga: 0 };
  fixtureDays.forEach((day) => {
    const matches = Array.isArray(day?.matches) ? day.matches : [];
    matches.forEach((match) => {
      const homeGoals = finiteGoals(match.home_goals);
      const awayGoals = finiteGoals(match.away_goals);
      if (homeGoals == null || awayGoals == null) return;
      if (teamId(match.home_team) === id) {
        gf += homeGoals;
        ga += awayGoals;
      } else if (teamId(match.away_team) === id) {
        gf += awayGoals;
        ga += homeGoals;
      }
    });
  });
  return { gf, ga };
};

const matchGoals = (match) => ({
  home: finiteGoals(
    match?.home_goals ?? match?.homeGoals ?? match?.home_score ?? match?.homeScore
  ),
  away: finiteGoals(
    match?.away_goals ?? match?.awayGoals ?? match?.away_score ?? match?.awayScore
  ),
});

const nestedMatches = (day) => {
  if (Array.isArray(day?.matches)) return day.matches;
  if (day?.home_team || day?.away_team) return [day];
  return [];
};

const fixtureDayTime = (day) => {
  const raw = day?.match_date || day?.date || nestedMatches(day)[0]?.match_date;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

const dayIsComplete = (day) => {
  const matches = nestedMatches(day);
  if (!matches.length) return false;
  return matches.every((match) => {
    const { home, away } = matchGoals(match);
    return home != null && away != null;
  });
};

/** Same numbering as the fixtures slider: today's card in date order, not array order. */
export const matchdayProgress = (fixtureDays, { currentDate, timezone } = {}) => {
  if (!Array.isArray(fixtureDays) || !fixtureDays.length) {
    return { current: null, total: null };
  }
  const sorted = [...fixtureDays].sort((a, b) => fixtureDayTime(a) - fixtureDayTime(b));
  const dateLabels = [
    ...new Set(
      sorted
        .map((day) =>
          formatMatchDay(day?.match_date || day?.date || nestedMatches(day)[0]?.match_date, {
            timezone,
            format: DAY_FORMAT,
          })
        )
        .filter(Boolean)
    ),
  ];
  const total = dateLabels.length || sorted.length;
  if (!total) return { current: null, total: null };

  const todayLabel = currentDate
    ? formatMatchDay(currentDate, { timezone, format: DAY_FORMAT })
    : localToday(DAY_FORMAT);
  let dateIndex = dateLabels.findIndex((label) => label === todayLabel);
  if (dateIndex < 0 && dateLabels.length) {
    const today = moment(todayLabel, DAY_FORMAT);
    dateIndex = dateLabels.findIndex((label) =>
      moment(label, DAY_FORMAT).isSameOrAfter(today, 'day')
    );
    if (dateIndex < 0) dateIndex = dateLabels.length - 1;
  }
  const fromDate = dateIndex >= 0 ? dateIndex + 1 : null;

  const anyScores = sorted.some((day) =>
    nestedMatches(day).some((match) => {
      const { home, away } = matchGoals(match);
      return home != null && away != null;
    })
  );
  if (anyScores) {
    const firstOpen = sorted.findIndex((day) => !dayIsComplete(day));
    const fromScores = firstOpen === -1 ? total : firstOpen + 1;
    if (fromDate != null && fromDate > fromScores) {
      return { current: fromDate, total };
    }
    return { current: fromScores, total };
  }

  return { current: fromDate, total };
};

const playerLeagueId = (player) => {
  const league = player?.league;
  if (!league) return null;
  if (typeof league === 'string') return league;
  return league._id || null;
};

export const mergeSquadIntoPlayers = (players, squad, myTeam) => {
  const byId = new Map();
  (Array.isArray(players) ? players : []).forEach((player) => {
    if (player?._id) byId.set(String(player._id), player);
  });
  (Array.isArray(squad) ? squad : [])
    .filter((player) => player?._id && !player.loanedOut)
    .forEach((player) => {
      const id = String(player._id);
      const existing = byId.get(id) || {};
      const team = player.team || existing.team || myTeam || null;
      const goals =
        finiteGoals(player.goals_scored) ?? finiteGoals(existing.goals_scored);
      const assists =
        finiteGoals(player.assists) ?? finiteGoals(existing.assists);
      const starts =
        finiteGoals(player.played_matches) ??
        finiteGoals(existing.played_matches);
      byId.set(id, {
        ...existing,
        ...player,
        team,
        goals_scored: goals,
        assists,
        played_matches: starts,
        fav_position: player.fav_position || existing.fav_position,
      });
    });
  return [...byId.values()];
};

export const leagueScorers = (
  players,
  { leagueId, teamIds, limit = 10, sort = 'g' } = {}
) => {
  if (!Array.isArray(players) || !players.length) {
    return { available: false, rows: [] };
  }
  const withStats = players.filter(
    (player) =>
      finiteGoals(player.goals_scored) != null ||
      finiteGoals(player.assists) != null
  );
  if (!withStats.length) return { available: false, rows: [] };

  const ids = teamIds instanceof Set ? teamIds : null;
  const compare =
    sort === 'a'
      ? (a, b) => b.a - a.a || b.g - a.g || a.name.localeCompare(b.name)
      : sort === 'ga'
        ? (a, b) => b.ga - a.ga || b.g - a.g || a.name.localeCompare(b.name)
        : (a, b) => b.g - a.g || b.ga - a.ga || a.name.localeCompare(b.name);

  const rows = withStats
    .filter((player) => {
      const tid = teamId(player.team);
      if (ids && ids.size) return Boolean(tid && ids.has(tid));
      if (!tid) return false;
      const lid = playerLeagueId(player);
      if (leagueId && lid) return String(lid) === String(leagueId);
      return true;
    })
    .map((player) => {
      const g = finiteGoals(player.goals_scored) || 0;
      const a = finiteGoals(player.assists) || 0;
      const rawPos = player.fav_position;
      return {
        id: playerRecordId(player._id || player.id),
        name: [player.first_name, player.last_name].filter(Boolean).join(' '),
        teamName: player.team?.name || '',
        teamId: player.team?._id,
        symbol: player.team?.symbol || '',
        pos: simplifiedPosition[rawPos] || rawPos || '',
        app: finiteGoals(player.played_matches),
        g,
        a,
        ga: g + a,
      };
    })
    .sort(compare)
    .map((row, index) => ({ ...row, rank: index + 1 }));

  return {
    available: true,
    rows: limit == null ? rows : rows.slice(0, limit),
  };
};

const resultLetter = (gf, ga) => {
  if (gf > ga) return 'W';
  if (gf < ga) return 'L';
  return 'D';
};

/** Last N finished league results per team, oldest → newest. Also counts clean sheets. */
export const leagueForm = (fixtureDays, lastN = 5) => {
  const byTeam = {};
  const ensure = (id) => {
    if (!byTeam[id]) byTeam[id] = { results: [], cleanSheets: 0 };
    return byTeam[id];
  };
  const days = Array.isArray(fixtureDays) ? [...fixtureDays] : [];
  days.sort((a, b) =>
    String(a?.match_date || '').localeCompare(String(b?.match_date || ''))
  );
  days.forEach((day) => {
    (day?.matches || []).forEach((match) => {
      const homeGoals = finiteGoals(match.home_goals);
      const awayGoals = finiteGoals(match.away_goals);
      if (homeGoals == null || awayGoals == null) return;
      const homeId = teamId(match.home_team);
      const awayId = teamId(match.away_team);
      if (homeId) {
        const row = ensure(homeId);
        row.results.push(resultLetter(homeGoals, awayGoals));
        if (awayGoals === 0) row.cleanSheets += 1;
      }
      if (awayId) {
        const row = ensure(awayId);
        row.results.push(resultLetter(awayGoals, homeGoals));
        if (homeGoals === 0) row.cleanSheets += 1;
      }
    });
  });
  Object.keys(byTeam).forEach((id) => {
    byTeam[id].results = byTeam[id].results.slice(-lastN);
  });
  return byTeam;
};

export const tableRowZone = (position, teamCount, division) => {
  const place = Number(position);
  const total = Number(teamCount);
  if (!place || !total || !division) return null;
  const top = Math.min(ZONE_SIZE, total);
  const bottomStart = total - Math.min(ZONE_SIZE, total) + 1;
  const inTop = place <= top;
  const inBottom = place >= bottomStart;
  if (division === 1) {
    if (place === 1) return 'champion';
    if (inBottom) return 'relegation';
    return null;
  }
  if (division === 2) {
    if (inTop) return 'promotion';
    if (inBottom) return 'relegation';
    return null;
  }
  if (division === 3 && inTop) return 'promotion';
  return null;
};
